import dayjs, { Dayjs } from 'dayjs'
import type { TFunction } from 'i18next'
import { CleaningStatusType, ReservationType, RoomSortKey, RoomType, RoomsDataType } from '@/constants/cleaning-manager'
import { MemoWithCategory, isCleaningMemo } from '@/models/reservation/reservation-note'

/**
 * roomCheckinAt はタイムゾーンを持たない 'YYYY-MM-DD HH:mm:ss' で返る。
 * 端末のタイムゾーンで解釈すると日付が前後するため、日付の比較・差分はすべて
 * 日付部分の文字列を基準にする。この関数を経由しない `dayjs(roomCheckinAt)` での
 * 日付比較を増やさないこと。
 */
const checkinDateOf = (roomCheckinAt: string): string => roomCheckinAt.slice(0, 10)

/**
 * IN 時刻を表示するのは、予約のチェックイン日が対象日と一致するときのみ。
 * 連泊中も roomCheckinAt には初日の IN 時刻が返るため、2泊目以降は表示しない（BE の帳票と同一ルール）。
 */
export const isCheckinDateOf = (roomCheckinAt: string | null | undefined, dateOf: Dayjs): boolean => {
  return !!roomCheckinAt && checkinDateOf(roomCheckinAt) === dateOf.format('YYYY-MM-DD')
}

/**
 * 対象日にチェックイン予定がある場合のみ IN 時刻を返す。表示しない場合は fallback を返す。
 */
export const formatCheckinTimeOfDate = (roomCheckinAt: string | null | undefined, dateOf: Dayjs, fallback: string): string => {
  return isCheckinDateOf(roomCheckinAt, dateOf) ? dayjs(roomCheckinAt as string).format('HH:mm') : fallback
}

/**
 * 対象日が滞在の何泊目かを 1 始まりで返す。チェックイン日当日が 1 泊目。
 * 日付だけを同じ形式で解釈するため、端末のタイムゾーンに依存しない。
 */
export const stayNightIndexOfDate = (roomCheckinAt: string, dateOf: Dayjs): number => {
  return dayjs(dateOf.format('YYYY-MM-DD')).diff(dayjs(checkinDateOf(roomCheckinAt)), 'day') + 1
}

/**
 * 「MM月DD日(ddd) N泊 - M泊目」の滞在期間ラベル。
 * PC / タブレット / スマホの3画面が同じ表記を出すため、整形をここに集約する。
 */
export const formatStayPeriodLabel = (reservation: ReservationType | null | undefined, dateOf: Dayjs, t: TFunction): string => {
  const roomCheckinAt = reservation?.roomCheckinAt
  if (!roomCheckinAt) {
    return '-'
  }

  const checkinDateLabel = dayjs(roomCheckinAt).format(t('cleaningManager.MM-DD(ddd)'))
  const nightsLabel = t('cleaningManager.Night', { nights: reservation?.nights })

  return `${checkinDateLabel} ${nightsLabel} - ${stayNightIndexOfDate(roomCheckinAt, dateOf)}${t('night(s)')}`
}

const checkinAtOfDate = (room: RoomType, dateOf: Dayjs): string | null => {
  const checkinAt = room.nextReservation?.roomCheckinAt
  return checkinAt && isCheckinDateOf(checkinAt, dateOf) ? checkinAt : null
}

// 当日チェックインの客室を先頭に、到着時間の早い順に並べる
const compareByCheckinAt = (a: RoomType, b: RoomType, dateOf: Dayjs): number => {
  const checkinAtA = checkinAtOfDate(a, dateOf)
  const checkinAtB = checkinAtOfDate(b, dateOf)

  if (checkinAtA && checkinAtB) {
    // 同一フォーマットの日時文字列のため、辞書順がそのまま時刻順になる
    return checkinAtA.localeCompare(checkinAtB)
  }
  if (checkinAtA) {
    return -1
  }
  if (checkinAtB) {
    return 1
  }
  return 0
}

/**
 * スマホ一覧に出す客室を、担当者・清掃ステータスで絞り込んでから並べる。
 *
 * RoomSortKey.RoomNumber は並べ替えない。API がフロア順・部屋表示順で返すため、
 * 絞り込んだだけの順序がそのまま「部屋番号の表示順」になる。
 * Array.prototype.sort は安定ソートなので、CheckinAt でも当日 IN の無い客室は
 * この表示順を保ったまま後方に集まる。
 */
export const buildCleaningRoomList = (
  rooms: RoomsDataType,
  dateOf: Dayjs,
  filters: { cleaningStaffId: string; cleaningStatus: CleaningStatusType | undefined },
  sortKey: RoomSortKey,
): RoomType[] => {
  const roomList = Object.values(rooms)
    .flat()
    .filter(room => !filters.cleaningStaffId || room.cleaningStaffId === filters.cleaningStaffId)
    .filter(room => filters.cleaningStatus === undefined || room.cleaningStatus === filters.cleaningStatus)

  return sortKey === RoomSortKey.CheckinAt ? roomList.sort((a, b) => compareByCheckinAt(a, b, dateOf)) : roomList
}

/**
 * 保存時に BE が付けた英語列を表示する。英語が空なら日本語に落とす。
 * 編集欄には使わない。英訳を保存すると原文が上書きされる。
 */
export const pickTranslatedText = (ja: string | undefined | null, en: string | undefined | null, language: string): string => {
  if (language === 'en' && en) {
    return en
  }
  return ja ?? ''
}

export const displayCleaningMemo = (notes: MemoWithCategory[] | undefined, language: string): string => {
  const note = notes?.find(({ category }) => isCleaningMemo(category))
  return pickTranslatedText(note?.memo, note?.memoEn, language)
}

export type CleaningListPreference = {
  sortKey: RoomSortKey
  // undefined は絞り込みなし。CleaningStatusType.Unused(0) と区別する必要がある
  cleaningStatus: CleaningStatusType | undefined
}

export const defaultCleaningListPreference: CleaningListPreference = {
  sortKey: RoomSortKey.CheckinAt,
  cleaningStatus: undefined,
}

const toSortKey = (value: unknown): RoomSortKey => {
  return Object.values<unknown>(RoomSortKey).includes(value) ? (value as RoomSortKey) : defaultCleaningListPreference.sortKey
}

const toCleaningStatus = (value: unknown): CleaningStatusType | undefined => {
  return Object.values<unknown>(CleaningStatusType).includes(value) ? (value as CleaningStatusType) : undefined
}

/**
 * localStorage に入っている値は、旧形式・手で壊された JSON・型違いのいずれもありうる。
 * 解釈できない値は既定値へ落とし、例外を画面まで伝播させない。
 */
export const parseCleaningListPreference = (stored: string | null): CleaningListPreference => {
  if (!stored) {
    return defaultCleaningListPreference
  }

  try {
    const parsed = JSON.parse(stored)
    return { sortKey: toSortKey(parsed?.sortKey), cleaningStatus: toCleaningStatus(parsed?.cleaningStatus) }
  } catch {
    return defaultCleaningListPreference
  }
}
